import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
} from '@mui/material';
import LockIconWrapper from '../components/LockIconWrapper';
import { toast } from 'react-toastify';
import { FormControlLabel, Switch } from '@mui/material';
import { validateEmail, validateZipCode, validateRequired } from '../../util/validation';

import AuthContext from '../../context/authContext';

function InvoiceAddressDialog({
  open,
  onClose,
  invoice,
  onSave,
  updateInvoiceCustomer,
  reissueWithCancellation,
}) {
  const { checkRightAny } = useContext(AuthContext);
  const [form, setForm] = useState({
    company: '',
    firstName: '',
    lastName: '',
    mail: '',
    street: '',
    postcode: '',
    city: '',
    addressAddition: '',
    jobCode: '',
  });
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [reissue, setReissue] = useState(false);

  useEffect(() => {
    if (invoice) {
      setForm({
        company: invoice.customerData?.company || '',
        firstName: invoice.customerData?.firstName || '',
        lastName: invoice.customerData?.lastName || '',
        mail: invoice.customerData?.mail || '',
        street: invoice.customerData?.street || '',
        postcode: invoice.customerData?.postcode || '',
        city: invoice.customerData?.city || '',
        addressAddition: invoice.customerData?.addressAddition || '',
        jobCode: invoice.customerData?.jobCode || '',
      });
      setErrors({});
    }
  }, [invoice]);

  const validateField = (name, value) => {
    switch (name) {
      // firstName and lastName are optional for invoice customer data
      case 'street':
        return validateRequired(value, 'Straße').isValid ? '' : 'Straße ist erforderlich';
      case 'postcode': {
        const r = validateZipCode(value);
        return r.isValid ? '' : r.error;
      }
      case 'city':
        return validateRequired(value, 'Ort').isValid ? '' : 'Ort ist erforderlich';
      case 'mail': {
        const r = validateEmail(value);
        return r.isValid ? '' : r.error;
      }
      default:
        return '';
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    const msg = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: msg }));
    setDirty(true);
  };

  const handleSubmit = async () => {
    // Validate all required fields before submitting
    const nextErrors = {
      street: validateField('street', form.street),
      postcode: validateField('postcode', form.postcode),
      city: validateField('city', form.city),
      mail: form.mail ? validateField('mail', form.mail) : '',
    };
    setErrors(nextErrors);
    const hasErrors = Object.values(nextErrors).some(Boolean);
    if (hasErrors) {
      toast.error('Bitte prüfe die Eingaben.');
      return;
    }

    try {
      if (reissue) {
        const result = await reissueWithCancellation(invoice._id, form);
        toast.success('Rechnung storniert und neu ausgestellt.');
        onSave?.(result.newInvoice);
      } else {
        const updated = await updateInvoiceCustomer(invoice._id, form);
        toast.success('Adresse erfolgreich aktualisiert.');
        onSave?.(updated);
      }
      onClose();
    } catch (err) {
      toast.error(
        reissue
          ? 'Fehler beim Neu-Ausstellen der Rechnung.'
          : 'Fehler beim Aktualisieren der Adresse.'
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Adresse bearbeiten</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {checkRightAny('invoice:special:reissueWithCancellation') && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={reissue} onChange={e => setReissue(e.target.checked)} />}
                label='Rechnung stornieren und neu ausstellen'
              />
            </Grid>
          )}
          {checkRightAny('invoice:special:editReceiver') && (
            <>
              <Grid item xs={12}>
                <TextField
                  label='Firma'
                  name='company'
                  fullWidth
                  value={form.company}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label='Vorname'
                  name='firstName'
                  fullWidth
                  value={form.firstName}
                  onChange={handleChange}
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName || ''}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label='Nachname'
                  name='lastName'
                  fullWidth
                  value={form.lastName}
                  onChange={handleChange}
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName || ''}
                />
              </Grid>
            </>
          )}
          {/* Address addition and reference (job code) editable with update:customerData */}
          <Grid item xs={12}>
            <TextField
              label='Adresszusatz'
              name='addressAddition'
              fullWidth
              value={form.addressAddition}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Ihre Referenz (Jobcode)'
              name='jobCode'
              fullWidth
              value={form.jobCode}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              type='email'
              label='E-Mail'
              name='mail'
              fullWidth
              value={form.mail}
              onChange={handleChange}
              error={Boolean(errors.mail)}
              helperText={errors.mail || ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Straße und Nr.'
              name='street'
              fullWidth
              value={form.street}
              onChange={handleChange}
              error={Boolean(errors.street)}
              helperText={errors.street || ''}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label='PLZ'
              name='postcode'
              fullWidth
              value={form.postcode}
              onChange={handleChange}
              error={Boolean(errors.postcode)}
              helperText={errors.postcode || ''}
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              label='Ort'
              name='city'
              fullWidth
              value={form.city}
              onChange={handleChange}
              error={Boolean(errors.city)}
              helperText={errors.city || ''}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={
            !dirty || Boolean(errors.street || errors.postcode || errors.city || errors.mail)
          }
        >
          <LockIconWrapper right='invoice:update:customerData'>Speichern</LockIconWrapper>
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InvoiceAddressDialog;
